import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BLOG_POSTS } from "../lib/data/blog";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import EditorialMarquee from "../components/site/EditorialMarquee";
import { setSEO } from "../lib/data/seo";

export default function Blog() {
  useEffect(() => {
    setSEO({
      title: "BC & Canadian Security Industry Journal — Maze Security Solutions",
      description:
        "Field-tested reporting on the BC and Canadian security industry — compliance, mining, cannabis, events, and fire watch — written by operators for operators.",
      keywords: BLOG_POSTS.flatMap((p) => p.keywords),
      canonical: `${window.location.origin}/blog`,
    });
  }, []);

  const [featured, ...rest] = BLOG_POSTS;

  return (
    <div className="pt-24">
      <section className="pt-16 md:pt-24 pb-16 bg-[#F4F4F0]" data-testid="blog-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Journal · Field Notes · {BLOG_POSTS.length} Posts
          </div>
          <RevealLines
            lines={["Written by operators.", "Read by operators."]}
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] text-[#060913]"
          />
          <p className="mt-10 max-w-2xl text-lg md:text-xl text-[#5C5F66] leading-relaxed">
            Long-form reporting on the BC and Canadian security industry —
            compliance, mining, cannabis, event operations, fire watch — with
            checklists you can send to any vendor.
          </p>
        </div>
      </section>

      <EditorialMarquee items={["COMPLIANCE", "MINING", "CANNABIS", "FIRE WATCH", "EVENTS", "WORKSAFEBC"]} />

      {/* Featured */}
      <section className="bg-[#F4F4F0] py-16 md:py-24" data-testid="blog-featured">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center border-y border-black/10 py-10"
            >
              <div className="md:col-span-6">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                </div>
              </div>
              <div className="md:col-span-6">
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                  Featured · {featured.category}
                </div>
                <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95] text-[#060913] group-hover:text-[#D92323] transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-[#5C5F66] text-base md:text-lg leading-relaxed max-w-xl">
                  {featured.excerpt}
                </p>
                <div className="mt-6 flex items-center gap-4 font-mono text-[10px] tracking-[0.22em] uppercase text-black/50">
                  <span>{featured.date}</span>
                  <span>·</span>
                  <span>{featured.readTime}</span>
                  <span>·</span>
                  <span>{featured.author}</span>
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* List */}
      <section className="bg-[#F4F4F0] pb-24" data-testid="blog-list">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="border-t border-black/10">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.04}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-row-${p.slug}`}
                  className="group grid grid-cols-12 gap-4 items-baseline py-8 md:py-10 border-b border-black/10 hover:bg-white transition-colors"
                >
                  <div className="col-span-2 md:col-span-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#D92323]">
                    {p.category}
                  </div>
                  <div className="col-span-10 md:col-span-8 font-display text-2xl md:text-4xl tracking-tight leading-tight text-[#060913] group-hover:text-[#D92323] transition-colors">
                    {p.title}
                  </div>
                  <div className="col-span-6 md:col-span-2 font-mono text-[10px] tracking-[0.22em] uppercase text-black/50">
                    {p.readTime}
                  </div>
                  <div className="col-span-6 md:col-span-1 text-right font-mono text-[11px] tracking-[0.22em] uppercase text-black/50 group-hover:text-[#D92323]">
                    →
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
